<template>
  <v-card>
    <v-card-title class="d-flex align-center justify-space-between">
      <div>
        <v-icon left>mdi-table</v-icon>
        Channel Comparison
      </div>
    </v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <div v-else-if="!timelineData" class="text-center py-8 text-grey">
        No timeline data available
      </div>
      <div v-else class="sparkline-charts">
        <!-- Channel comparison table -->
        <v-row class="mt-6">
          <v-col cols="12">
            <v-table>
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Total Quotes</th>
                  <th>Average/Day</th>
                  <th>Peak Day</th>
                  <th>Peak Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="channel in timelineData.channels" :key="channel.id">
                  <td>
                    <div class="d-flex align-center">
                      <div
                        class="me-2"
                        :style="{
                          width: '12px',
                          height: '12px',
                          backgroundColor: channel.color,
                          borderRadius: '2px',
                        }"
                      ></div>
                      {{ channel.name }}
                    </div>
                  </td>
                  <td>{{ getChannelTotal(channel.id) }}</td>
                  <td>{{ getChannelAverage(channel.id) }}</td>
                  <td>{{ getChannelPeakDay(channel.id) }}</td>
                  <td>{{ getChannelPeakValue(channel.id) }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { statisticsService } from "../services";

export default {
  name: "ChannelComparisonTable",
  props: {
    startDate: { type: [String, Date], required: true },
    endDate: { type: [String, Date], required: true },
  },
  data() {
    return {
      loading: false,
      timelineData: null,
    };
  },
  computed: {
    chartLabels() {
      if (!this.timelineData) return [];
      return this.timelineData.timeline.map((day) =>
        new Date(day.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      );
    },
  },
  watch: {
    startDate: { immediate: true, handler() { this.loadTimelineData(); } },
    endDate: { immediate: true, handler() { this.loadTimelineData(); } },
  },
  methods: {
    async loadTimelineData() {
      this.loading = true;
      try {
        const startYmd = this.toYmd(this.startDate);
        const endYmd = this.toYmd(this.endDate);
        const response = await statisticsService.getQuotesTimeline({
          period: 'custom',
          startIso: startYmd,
          endIso: endYmd
        });
        if (response.success) {
          this.timelineData = response.data;
        } else if (response?.timeline) {
          this.timelineData = response;
        } else {
          this.timelineData = null;
        }
      } catch (error) {
        console.error("Error loading timeline data:", error);
        this.timelineData = null;
      } finally {
        this.loading = false;
      }
    },
    toYmd(d) {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },

    getChannelData(channelId) {
      if (!this.timelineData) return [];
      return this.timelineData.timeline.map(
        (day) => day.channels[channelId] || 0
      );
    },

    getCombinedData() {
      if (!this.timelineData) return [];
      return this.timelineData.timeline.map((day) => {
        return Object.values(day.channels).reduce(
          (sum, value) => sum + value,
          0
        );
      });
    },

    getChannelStats(channelId) {
      const data = this.getChannelData(channelId);
      const total = data.reduce((sum, value) => sum + value, 0);
      const avg = data.length > 0 ? (total / data.length).toFixed(1) : 0;
      return `${avg} avg/day`;
    },

    getChannelTotal(channelId) {
      const data = this.getChannelData(channelId);
      return data.reduce((sum, value) => sum + value, 0);
    },

    getChannelAverage(channelId) {
      const data = this.getChannelData(channelId);
      const total = data.reduce((sum, value) => sum + value, 0);
      return data.length > 0 ? (total / data.length).toFixed(1) : 0;
    },

    getChannelPeakDay(channelId) {
      const data = this.getChannelData(channelId);
      const maxIndex = data.indexOf(Math.max(...data));
      if (maxIndex >= 0 && this.timelineData) {
        return new Date(
          this.timelineData.timeline[maxIndex].date
        ).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }
      return "N/A";
    },

    getChannelPeakValue(channelId) {
      const data = this.getChannelData(channelId);
      return Math.max(...data);
    },

    getTotalQuotes() {
      return this.getCombinedData().reduce((sum, value) => sum + value, 0);
    },
  },
};
</script>

<style scoped>
.sparkline-charts {
  margin-top: 20px;
}

.v-sheet--offset {
  top: -24px;
  position: relative;
}

/* Custom sparkline styling */
:deep(.v-sparkline) {
  height: 100px;
}

:deep(.v-sparkline__line) {
  stroke-linecap: round;
  stroke-linejoin: round;
}

:deep(.v-sparkline__area) {
  fill-opacity: 0.3;
}
</style>
